import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Grid, Typography, Box, CardContent, CardHeader, Card, Stack, Divider, Button, Tooltip, FormHelperText, FormControl, InputLabel, OutlinedInput } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import { Document } from '../../../../types/DocumentType';
import axios from '../../../../../services/ApiService';
import { Agent } from '../../../../types/UserTypes';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../../../support/LoadingSpinner';
import Swal from 'sweetalert2'
import { BVTConstants } from '../../../../../utils/BVTConstants';
import { useDocuments } from '../../../../../hooks/useDocuments';

function CompanyAgentView() {
  const navigate = useNavigate();
  const location = useLocation();
  const agentId = (location.state as { agentId?: number })?.agentId;
  const { showDocument, saveDocument } = useDocuments();
  const [error, setError] = useState<string>('');
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [verify, setVerify] = useState<boolean>(false);
  const [verificationComment, setVerificationComment] = useState('');
  const [verCommentError, setVerCommentError] = useState('');
  const [certError, setCertError] = useState('');
  const [agentData, setAgentData] = useState<Agent | null>(null);
  const [certificateData, setCertificateData] = useState<Document>({
    id: 0,
    fileName: '',
    purpose: '',
    contentFile: null,
    contentBytes: null,
    status: '',
    mimeType: '',
    createdAt: '',
    updatedAt: '',
  });
  useEffect(() => {
    fetchAgentData();
  }, []);

  const fetchAgentData = async () => {
    try {
      setDataLoading(true);
      setVerify(false);
      const response = await axios.post("/getAgent", agentId);
      setAgentData(response.data);
    } catch (error) {
      console.error('Error fetching agent data.');
    } finally {
      setDataLoading(false);
    }
  };

  const handleVerification = async () => {
    if(String(agentData?.isVerified) === 'true' && agentData?.certId) {
      setLoading(true);
      await showDocument(agentData?.certId);
      setLoading(false);
    } else {
      setVerificationComment('');
      setVerify(true);
    }
  }

  const saveVerification = async () => {
    setVerCommentError('');
    setCertError('');
    if (!verificationComment.trim()) {
      setVerCommentError('Verification comment is required.');
      return;
    }
    if (!certificateData.contentFile) {
      setCertError('Certificate is required.');
      return;
    }
    setLoading(true);
    try{
      const docId = await saveDocument(certificateData);
      console.log('Document saved successfully ' + docId);
      agentData.certId = docId;
      agentData.isVerified = true;
      agentData.verificationComment = verificationComment;
      await axios.post("/updateAgentProfile", agentData);
      toast.success("Agent verified successfully.");
      fetchAgentData();
    }catch (error) {
      console.error('Error saving agent.');
    } finally {
      setLoading(false);
    }
  }

  const updateStatusClick = async () => {
    const activeText: string = agentData?.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate';
    Swal.fire({title: activeText + ' Agent ?', 
      text: 'Do you want to '+ activeText +' the agent ' + agentData?.displayName + '?', 
      icon: 'question', 
      confirmButtonText: activeText, 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        const req = {id: agentData.userId, status: agentData.status === BVTConstants.ACTIVE ? BVTConstants.INACTIVE : BVTConstants.ACTIVE}
        changeStatus(req);
      }
    })
  }

  const archiveAgent = async () => {
    Swal.fire({title: 'Delete Agent ?', 
      text: 'Do you want to delete the agent ' + agentData?.displayName + '?', 
      icon: 'question', 
      confirmButtonText: 'Delete', 
      cancelButtonText: 'Cancel', 
      showCancelButton: true }
    ).then((result) => {
      if(result['isConfirmed']){
        const req = {id: agentData.userId, status: BVTConstants.ARCHIVED}
        changeStatus(req);
      }
    })
  }

  const changeStatus = async (req: any) => {
    try {
      setLoading(true);
      const response = await axios.post("/updateUserStatus", req);
      if(response.status === 200){
        toast.success('Agent updated successfully');
        if(req.status === BVTConstants.ARCHIVED){
          navigate(-1);
        }else{
          fetchAgentData();
        }
      }
    } catch (error) {
      console.error('Error updating agent.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
    <Helmet><title>BVT Company - Agents</title></Helmet>
    {dataLoading ? (
          <LoadingSpinner loading={dataLoading} />
        ) : error ? (
          <Typography variant="h6" color="error" textAlign="center">
            {error}
          </Typography>
        ) : (
        <Stack spacing={2}>
          <Card>
            <CardHeader 
              title={agentData?.displayName}
              subheader={agentData?.status}
              action={
                <>
                  {/* <Button
                      variant="outlined"
                      sx={{marginRight: 1}}
                      onClick={() => handleVerification()}
                    >
                      {String(agentData?.isVerified) === 'true' ? 'View Certificate' : 'Verify Agent'}
                  </Button> */}
                  {String(agentData?.isPrimary) === 'false' && String(agentData?.status) !== BVTConstants.PARENT_INACTIVE &&(
                  <Button
                      variant="outlined"
                      sx={{marginRight: 1}}
                      onClick={() => updateStatusClick()}
                    >
                      {agentData?.status === BVTConstants.ACTIVE ? 'Deactivate' : 'Activate'}
                  </Button>
                  )}
                  {/* {String(agentData?.isPrimary) === 'false' && (
                  <Tooltip title="Archive Agent" arrow>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => archiveAgent()}
                      >
                        <DeleteOutlineIcon/>
                      </Button>
                  </Tooltip>
                  )} */}
                  <IconButton 
                    aria-label="close" 
                    onClick={() => navigate(-1)}
                    sx={{ 
                      border: '1px solid',
                      borderColor: 'divider',
                      ml: 1
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </>
              }
              />
            <Divider />
            <CardContent>
              <Box sx={{ maxWidth: '900px' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      First Name
                    </Typography>
                    <Typography variant="body1">{agentData?.firstName}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Last Name
                    </Typography>
                    <Typography variant="body1">{agentData?.lastName}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Email
                    </Typography>
                    <Typography variant="body1">{agentData?.email}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Contact No.
                    </Typography>
                    <Typography variant="body1">{agentData?.contact}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Company
                    </Typography>
                    <Typography variant="body1">{agentData?.companyName}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Address
                    </Typography>
                    <Typography variant="body1">{agentData?.address}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Member Since
                    </Typography>
                    <Typography variant="body1">{agentData?.createdAt}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Last Login
                    </Typography>
                    <Typography variant="body1">{agentData?.lastLoginAt}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Is Verified
                    </Typography>
                    <Typography variant="body1">{String(agentData?.isVerified) === 'true' ? 'Yes' : 'No'}</Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Verification Comment
                    </Typography>
                    <Typography variant="body1">{agentData?.verificationComment ? agentData?.verificationComment : 'N/A'}</Typography>
                  </Grid>

                  {verify && (
                    <>
                    <Divider />
                    <Grid item xs={12} md={7} mt={1}>
                        <FormControl fullWidth error={!!verCommentError}>
                                <InputLabel>Verification Comment</InputLabel>
                                <OutlinedInput 
                                    label="Verification Comment" 
                                    name="verificationComment"
                                    value={verificationComment}
                                    inputProps={{ maxLength: 100 }}
                                    onChange={(event) => setVerificationComment(event.target.value)}
                                />
                                {verCommentError && (
                                    <FormHelperText error>{verCommentError}</FormHelperText>
                                )}
                            </FormControl>
                    </Grid>
                    <Grid item xs={12} md={3} mt={1}>
                        <Button
                                variant="contained"
                                component="label"
                                fullWidth
                            >
                                Upload Certificate
                                <input
                                    type="file"
                                    hidden
                                    accept={BVTConstants.FILE_TYPES}
                                    onChange={(event) => {
                                        if (event.target.files && event.target.files.length > 0) {
                                            const file = event.target.files[0];
                                            
                                            // Check file size
                                            if (file.size > BVTConstants.FILE_SIZE_500_KB_LIMIT) {
                                                setCertError('File size should not exceed 500 KB');
                                                return;
                                            }
                                            
                                            // Check file format
                                            const allowedTypes = BVTConstants.FILE_TYPES.split(',');
                                            const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
                                            const isValidFormat = allowedTypes.some(type => 
                                                type.toLowerCase() === fileExtension
                                            );
                                            
                                            if (!isValidFormat) {
                                                setCertError(`Invalid file format. Allowed formats: ${BVTConstants.FILE_TYPES}`);
                                                return;
                                            }
                                            
                                            setCertificateData(prev => ({
                                                ...prev,
                                                contentFile: file,
                                                fileName: file.name,
                                                purpose: BVTConstants.AGENT_CERTIFICATE
                                            }));
                                            setCertError('');
                                        }
                                    }}
                                />
                            </Button>
                            {certError && (
                                    <FormHelperText error>{certError}</FormHelperText>
                                )}
                    </Grid>
                    <Grid item xs={12} md={2} mt={1}>
                        <Button
                                variant="contained"
                                component="label"
                                onClick={() => saveVerification()}
                                fullWidth
                            >
                                Verify
                            </Button>
                    </Grid>
                    </>
                    )}
                </Grid>
              </Box>
            </CardContent>
          </Card>
          <LoadingSpinner loading={loading} />
        </Stack>
        )}
    </>
  )
}

export default CompanyAgentView